import type { ThemeVariant } from '../../contract.js';
import { defaultLight, defaultDark } from '../default/variants.js';

/**
 * Cartographic family — bespoke earthy palette (post-redesign).
 *
 * Original shipment inherited Default's variants entirely
 * (`variants: { light: defaultLight, dark: defaultDark }`),
 * meaning Cartographic was visually identical to Initiative
 * with only the mapStyle distinguishing them. This redesign
 * gives Cartographic its own identity:
 *
 *   - Light variant: warm daylight palette — parchment / cream
 *     surfaces, dark-espresso text, forest-moss accent. The
 *     "explorer reading a paper field-map at midday" mental
 *     model the Outdoors basemap implies.
 *   - Dark variant: deep-forest-dusk palette — very dark
 *     espresso surfaces, warm cream text, amber accent. The
 *     "explorer at the campfire after sunset" companion.
 *
 * `charging` stays #21c163 on both variants (brand-green
 * literal for session-state UI only, same pattern as every
 * other theme post-greyscale-rework).
 *
 * Adaptations from Default:
 *   - mute / textMute shifted from cool greys to warm
 *     brown-greys matching the earth palette
 *   - widget + chrome glass recipes warm-toned (brown drop
 *     shadow instead of black; accent ring uses the new earthy
 *     accent instead of Default's brand green)
 *   - info / warn / busy / power kept at semantic-canon values
 *     (blue / red / amber / mustard) — these are recognition
 *     cues for status states and shouldn't shift just because
 *     the chrome went warm. `good` kept distinct from accent
 *     (good is a brighter green, accent is forest-moss) so the
 *     two channels read separately when both appear on the
 *     same surface (e.g. an active CTA next to a success pill).
 *
 * Token-name substitutions: the build-prompt spec included
 * `sidebarItem` / `sidebarItemActive` / `sidebarItemHover`
 * names that don't exist on `ThemeColors`. Same substitution
 * pattern as the Electric Light Dark reframe:
 *
 *   sidebar              ← spec `sidebar`
 *   sidebarText          ← derived (dark on cream / cream on dark)
 *   sidebarMute          ← derived (matches textMute hue)
 *   sidebarActive        ← spec `sidebarItemActive`
 *
 * The spec's `sidebarItem` (idle item bg) and `sidebarItemHover`
 * (hover bg) don't map onto any existing contract field. If
 * admin-sidebar UI ever needs per-item bg / hover states as
 * theme tokens, a separate contract extension would be the
 * right path. Flagged in the commit body.
 *
 * Sidebar mode-flip: Warren's spec calls for a light sidebar
 * on the Light variant and a dark sidebar on the Dark variant.
 * This BREAKS the documented "sidebar palette stays shared
 * across mode" convention (defaultLight + defaultDark both
 * use the same SIDEBAR_DARK const, ditto Luce's SIDEBAR_LUCE).
 * Cartographic joins Electric Light as the second family to
 * intentionally flip its sidebar with mode. Driver PWA doesn't
 * render a sidebar so the asymmetry only matters in the admin
 * shell. Flagged in the commit body.
 */
export const cartographicLight: ThemeVariant = {
  mode: 'light',
  tokens: {
    ...defaultLight.tokens,
    colors: {
      ...defaultLight.tokens.colors,
      // Parchment + cream surface stack.
      bg: '#F4EDE0',
      surface: '#FAF4E8',
      surface2: '#EDE4D4',
      // Light sidebar — warm cream, dark espresso text. Breaks the
      // "sidebar doesn't flip per mode" convention for this family
      // (see file-header comment); flagged in commit body.
      sidebar: '#F0E8DA',
      sidebarText: '#2F2418',
      sidebarMute: '#7B6F5E',
      sidebarActive: '#5C7C3A',
      border: '#D4C4A8',
      borderStrong: '#B8A586',
      // Forest moss accent — earthy, not the bright EVI green. The
      // `charging` token below stays brand green for literal
      // session-state UI only.
      accent: '#5C7C3A',
      accentSoft: 'rgba(92, 124, 58, 0.14)',
      // Dark-luminance accent → cream text on CTA. Matches bg so
      // primary-CTA text reads with body-paper hue against the
      // forest-moss button.
      onAccent: '#F4EDE0',
      charging: '#21c163',
      chargingGlow: 'rgba(92, 124, 58, 0.45)',
      // Semantic colours — keep canon hues for status recognition
      // (info=blue, warn=red, busy=amber, power=mustard). `good`
      // bumped slightly from defaultLight's #16A34A so it stays
      // visually distinct from the new forest-moss accent (which
      // is a darker, more olive green). Without this, both
      // channels collide on surfaces that render an active CTA
      // next to a success pill.
      good: '#3F8B2F',
      mute: '#8A7F6E',
      text: '#2F2418',
      textMute: '#7B6F5E',
      // Forest-moss 3-stop card glow stack. Lower alphas than
      // Default light's recipe because the moss hue is darker than
      // Default's #16A34A — equivalent visual weight at lower
      // tint values.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #5C7C3A 18%, transparent),'
        + ' 0 0 18px -4px color-mix(in srgb, #5C7C3A 22%, transparent),'
        + ' 0 6px 20px -10px color-mix(in srgb, #5C7C3A 18%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #5C7C3A 8%, transparent),'
        + ' 0 0 10px -6px color-mix(in srgb, #5C7C3A 12%, transparent),'
        + ' 0 3px 10px -8px color-mix(in srgb, #5C7C3A 8%, transparent)',
      // Widget + chrome glass — warm cream bg with brown drop
      // shadow + forest accent ring. Reduced blur (8px) inherited
      // from defaultLight's "heavy blur on bright basemap reads as
      // muddy fog" decision; same reasoning applies here since the
      // Outdoors basemap is a bright cartographic surface.
      widgetBg: 'rgba(250, 244, 232, 0.82)',
      widgetBorder: 'rgba(60, 40, 20, 0.10)',
      widgetShadow:
        '0 8px 24px rgba(60, 40, 20, 0.12),'
        + ' 0 0 0 1px rgba(92, 124, 58, 0.12)',
      chromeBg: 'rgba(250, 244, 232, 0.94)',
      chromeBorder: 'rgba(60, 40, 20, 0.08)',
      chromeShadow: '0 4px 16px rgba(60, 40, 20, 0.12)',
    },
  },
};

export const cartographicDark: ThemeVariant = {
  mode: 'dark',
  tokens: {
    ...defaultDark.tokens,
    colors: {
      ...defaultDark.tokens.colors,
      // Deep forest dusk surface stack — espresso brown rather
      // than Default dark's cool charcoal.
      bg: '#1F1A14',
      surface: '#28221A',
      surface2: '#332A20',
      // Dark sidebar — espresso, warm cream text. Same mode-flip
      // note as Light: this breaks the shared-sidebar convention
      // for this family (see file-header comment).
      sidebar: '#241F18',
      sidebarText: '#F0E4D0',
      sidebarMute: '#A89B7E',
      sidebarActive: '#C99A4B',
      border: '#3D3528',
      borderStrong: '#544938',
      // Warm amber gold accent — campfire / golden-hour feel.
      // Mid-luminance so both light and dark text read on it; the
      // `onAccent` override below picks the dark-espresso variant
      // to match the variant's bg hue.
      accent: '#C99A4B',
      accentSoft: 'rgba(201, 154, 75, 0.18)',
      // Dark espresso text on amber CTA — strong contrast (WCAG
      // ~8:1) and visually consistent with the variant's bg hue.
      onAccent: '#1F1A14',
      charging: '#21c163',
      chargingGlow: 'rgba(201, 154, 75, 0.55)',
      // Semantic colours — keep canon hues for status recognition.
      // `good` shifts slightly toward the amber-warm side so it
      // doesn't read as a separate green channel competing with
      // the brand `charging` indicator on the same surface;
      // canonical dark `good` of #34D399 (mint) feels disconnected
      // from this variant's warm palette.
      good: '#7EB85F',
      mute: '#6B5C45',
      text: '#F0E4D0',
      textMute: '#A89B7E',
      // Amber 3-stop card glow stack — same shape as Default
      // dark / Luce dark with the accent hue swapped to the
      // golden-hour amber. Higher alphas than the Light variant
      // because the dark surface needs more glow weight to read.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #C99A4B 22%, transparent),'
        + ' 0 0 24px -4px color-mix(in srgb, #C99A4B 34%, transparent),'
        + ' 0 8px 32px -12px color-mix(in srgb, #C99A4B 28%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #C99A4B 12%, transparent),'
        + ' 0 0 14px -6px color-mix(in srgb, #C99A4B 18%, transparent),'
        + ' 0 4px 12px -8px color-mix(in srgb, #C99A4B 14%, transparent)',
      // Widget + chrome glass — warm dark espresso bg with amber
      // ring tint. Same dense-glass blur (16px) as Default dark
      // since the Outdoors dark surface is a busy basemap.
      widgetBg: 'rgba(40, 30, 18, 0.78)',
      widgetBorder: 'rgba(255, 230, 200, 0.10)',
      widgetShadow:
        '0 12px 32px rgba(15, 10, 5, 0.50),'
        + ' 0 0 0 1px rgba(201, 154, 75, 0.12)',
      chromeBg: 'rgba(40, 30, 18, 0.92)',
      chromeBorder: 'rgba(255, 230, 200, 0.08)',
      chromeShadow: '0 8px 24px rgba(15, 10, 5, 0.55)',
    },
  },
};
